import { useState } from 'react';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { authHandlers } from 'api_handlers/auth';
import YoutubeLogin from 'components/auth/YoutubeLogin';
import FacebookLogin from 'components/auth/FacebookLoginButton';

import mixpanel from '../../../../utils/mixpanel';

const AuthLogin = ({
  goToDiscover,
  goToCreatorApp,
  showDomainInvalidModal,
  goToForgotPassword,
  ...others
}) => {
  const login = (e) => {
    e.preventDefault();
    setErrors('');
    setEmailError(false);
    setPasswordError(false);
    let isValidationError = false;
    if (!(email && email.length > 0)) {
        setEmailError(true);
        isValidationError = true;
    }
    if (!(password && password.length > 0)) {
        setPasswordError(true);
        isValidationError = true;
    }
    if (isValidationError) {
      return;
    }
    setIsSubmitting(true);
    const resp = authHandlers.login({
      email: email,
      password: password,
      user_type: 'brand',
    });

    resp.then(async (response) => {
        setIsSubmitting(false);
        if ([200, 201].includes(response.status)) {
            const userData = await response.json();
            localStorage.setItem('access', userData.access);
            localStorage.setItem('refresh', userData.refresh);
            const user = {
            first_name: userData.first_name,
            last_name: userData.last_name,
            email: userData.email,
            user_type: userData.user_type,
            influencer: userData.influencer,
            brand: userData.brand,
            is_superuser: userData.is_superuser,
            };
            localStorage.setItem('user', JSON.stringify(user));
            if (process.env.NODE_ENV === "production") {
              mixpanel.track("Sign-in");
            }
            goToDiscover();
        } else if ([400, 401, 402, 403].includes(response.status)) {
            const errors = await response.json();
            setErrors(errors);
        } else {
          setErrors({ 'non_field_errors': ['Please Try Again'] });
        }
    });
  }

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [errors, setErrors] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  
  /**
   * Instagram creator accounts authenticate through Facebook, and
   * /facebook/registration is a creator endpoint — there is no brand
   * equivalent — so this button signs the user into the creator app rather
   * than the brand planner the email form above leads to.
   */
  const registerToBackendFacebook = async (authResponse) => {
    setErrors('');
    const user = await authHandlers.facebookLogin(authResponse);
    if (user) {
      if (process.env.NODE_ENV === "production") {
        mixpanel.track("Sign-in");
      }
      goToCreatorApp();
    } else {
      setErrors({ 'non_field_errors': ['We could not sign you in with that Facebook account.'] });
    }
  }

  const registerToBackendYoutube = async(data) => {
    const resp_data = await authHandlers.youtubeBrandLogin(data);
    if (resp_data.error && resp_data.error === 'Please provide valid brand email') {
      showDomainInvalidModal();
    } else if (resp_data.user) {
      goToDiscover();
      if (process.env.NODE_ENV === "production") {
        mixpanel.track("Sign-in");
      }
    } else {
      setErrors({ 'non_field_errors': ['Please Try Again'] });
    }
  }

  return (
    <form className="bl-form" noValidate onSubmit={login}>
      <FormControl fullWidth error={emailError}>
        <OutlinedInput
          id="outlined-adornment-email-login"
          type="email"
          name="email"
          placeholder="Work Email Address"
          inputProps={{ 'aria-label': 'Work email address' }}
          onChange={(e) => {
            let value = e.target.value;
            setEmailError(false);
            if (!(value && value.length > 0)) {
              setEmailError(true);
            }
            setEmail(value);
          }}
        />
        {emailError && (
          <FormHelperText error id="standard-weight-helper-text-email-login">
            Please Enter Valid Email
          </FormHelperText>
        )}
      </FormControl>
      <FormControl fullWidth error={passwordError}>
        <OutlinedInput
          required
          id="outlined-adornment-password-login"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="Password"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                size="large"
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
          inputProps={{ 'aria-label': 'Password' }}
          onChange={(e) => {
            let value = e.target.value;
            setPasswordError(false);
            if (!(value && value.length > 0)) {
              setPasswordError(true);
            }
            setPassword(value);
          }}
        />
        {passwordError && (
          <FormHelperText error id="standard-weight-helper-text-email-login">
            Please Enter Valid Password
          </FormHelperText>
        )}
      </FormControl>

      <div className="bl-forgot">
        <button
          type="button"
          className="bl-link"
          onClick={() => {
            goToForgotPassword();
          }}
        >
          Forgot password?
        </button>
      </div>

      <button type="submit" className="bl-btn bl-btn--primary" disabled={isSubmitting}>
        {isSubmitting ? <span className="bl-spinner" aria-hidden="true" /> : null}
        {isSubmitting ? 'Signing you in…' : 'Sign in'}
      </button>

      {errors ? (
        <div className="bl-error" role="alert">
          {'non_field_errors' in errors ? (
            errors['non_field_errors'].map((each) => {
              return each;
            })
          ) : (
            <ul>
              {Object.keys(errors).map((key) => {
                const error = errors[key];
                if (Array.isArray(error)) {
                  return <li key={key}>{error}</li>;
                }
                return <li key={key}>{Object.values(error).join(',')}</li>;
              })}
            </ul>
          )}
        </div>
      ) : null}

      <div className="bl-divider">
        <span>OR CONTINUE WITH</span>
      </div>

      <div className="bl-social bl-social--google">
        <YoutubeLogin
          label="Continue with Google"
          onSuccess={(response) => {
            registerToBackendYoutube(response);
          }}
        />
      </div>

      <div className="bl-social bl-social--facebook">
        <FacebookLogin
          label="Continue with Facebook"
          onSuccess={(authResponse) => {
            registerToBackendFacebook(authResponse);
          }}
          onFailure={(error) => {
            setErrors({ non_field_errors: [error.message] });
          }}
        />
      </div>

      <p className="bl-hint">
        Instagram business and creator accounts sign in through Facebook.
      </p>
    </form>
  );
};

export default AuthLogin;
